import React, { useState, useEffect } from 'react';
import api from '../services/api';

const UserManagementTable = ({ onRunReport }) => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null); 
    const [filterText, setFilterText] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'totalValue', direction: 'desc' });

    const loadStats = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const res = await api.get('/admin/user-stats');
            setStats(res.data);
        } catch (err) {
            console.error("Failed to load user stats", err);
            const msg = err.response?.data?.msg || err.message || "Unknown error";
            setErrorMsg(`Failed to load data: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, []);

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const handleStatusToggle = async (userId, currentStatus, userName) => {
        const newStatus = currentStatus === 'approved' ? 'suspended' : 'approved';
        const actionLabel = newStatus === 'approved' ? 'REINSTATE' : 'SUSPEND';
        const confirmMsg = `Are you sure you want to ${actionLabel} user "${userName}"?\n\n${newStatus === 'suspended' ? 'User will be blocked from placing new bids.' : 'User will be allowed to bid again.'}`;
        
        if (!window.confirm(confirmMsg)) return;

        try {
            await api.put(`/admin/users/${userId}/status`, { status: newStatus });
            loadStats();
        } catch (err) {
            alert("Failed to update status");
        }
    };

    const handleRunReport = (userId) => {
        if (onRunReport) {
            onRunReport(userId);
        } else {
            alert("Report function not connected");
        }
    };

    const getProcessedData = () => {
        let data = stats.filter(row => {
            const search = filterText.toLowerCase();
            const sbiText = row.sbi ? row.sbi.toLowerCase() : ''; 
            return (
                row.userName.toLowerCase().includes(search) ||
                row.email.toLowerCase().includes(search) ||
                sbiText.includes(search)
            );
        });

        data.sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return data;
    };

    const processedData = getProcessedData();
    const adminRows = processedData.filter(row => row.role === 'admin');
    const generalRows = processedData.filter(row => row.role === 'user');

    if (loading) return <div style={{padding:'20px', textAlign:'center', color: '#475569', fontFamily: '"Inter", sans-serif'}}>Loading User Data...</div>;

    if (errorMsg) return (
        <div style={{padding:'20px', textAlign:'center', color: '#0F172A', backgroundColor:'#F8FAFC', borderRadius:'5px', border:'1px solid #F59E0B', fontFamily: '"Inter", sans-serif'}}>
            <strong>Error:</strong> {errorMsg}
            <br/><br/>
            <button onClick={loadStats} style={{...styles.actionBtn, backgroundColor: '#0D9488', color: '#FFFFFF'}}>Retry</button>
        </div>
    );

    const renderGroup = (title, rows) => (
        <div style={{ marginBottom: '25px' }}>
            <h3 style={{marginTop:0, marginBottom:'10px', color: '#163321', fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: '600', fontSize: '1.05rem'}}>{title}</h3>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: '1px solid #eee', borderRadius: '4px' }}>
                <table style={{width:'100%', borderCollapse:'collapse', backgroundColor: '#FFFFFF'}}>
                    <thead>
                        <tr style={{textAlign:'left'}}>
                            <th onClick={() => handleSort('userName')} style={styles.th}>Farmer<br/>{sortConfig.key==='userName' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th onClick={() => handleSort('totalBids')} style={styles.th}>Bids<br/>(Total) {sortConfig.key==='totalBids' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th onClick={() => handleSort('totalValue')} style={styles.th}>Value<br/>(Total) {sortConfig.key==='totalValue' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th onClick={() => handleSort('winningBids')} style={styles.th}>Wins<br/>{sortConfig.key==='winningBids' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th onClick={() => handleSort('winningValue')} style={styles.th}>Value<br/>(Won) {sortConfig.key==='winningValue' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th onClick={() => handleSort('lastBidDate')} style={styles.th}>Last<br/>Active {sortConfig.key==='lastBidDate' && (sortConfig.direction==='asc'?'▲':'▼')}</th>
                            <th style={styles.th}>Land Use<br/>(Ha, incl. ignored)</th>
                            <th style={{...styles.th, width:'200px'}}>Participation<br/>Status</th>
                            <th style={{...styles.th, textAlign:'right'}}>Bid<br/>History</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={row.userId} style={{borderBottom: '1px solid #eee', backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC'}}>
                                <td style={styles.td}>
                                    <strong>{row.userName}</strong><br/>
                                    <span style={{fontSize:'0.8rem', color:'#475569'}}>{row.email}</span>
                                </td>
                                <td style={styles.td}>{row.totalBids}</td>
                                <td style={styles.td}>£{row.totalValue.toLocaleString()}</td>
                                <td style={styles.td}><strong style={{color:'#0D9488'}}>{row.winningBids}</strong></td>
                                <td style={styles.td}><strong style={{color:'#0D9488'}}>£{row.winningValue.toLocaleString()}</strong></td>
                                <td style={styles.td}>{row.lastBidDate !== 'Never' ? new Date(row.lastBidDate).toLocaleDateString('en-GB') : '-'}</td>

                                <td style={styles.td}>
                                    {row.landUseHa ? (
                                        <div style={{fontSize:'0.75rem', lineHeight:'1.5'}}>
                                            <div>Arable: {row.landUseHa.arable.toFixed(2)}</div>
                                            <div>Grassland: {row.landUseHa.grassland.toFixed(2)}</div>
                                            <div>Woodland: {row.landUseHa.woodland.toFixed(2)}</div>
                                            <div>Other: {row.landUseHa.other.toFixed(2)}</div>
                                        </div>
                                    ) : '-'}
                                </td>

                                <td style={styles.td}>
                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                        <span style={{
                                            padding:'4px 8px', borderRadius:'4px', fontWeight:'bold', fontSize:'0.75rem',
                                            backgroundColor: row.status === 'approved' ? '#0D9488' : '#0F172A',
                                            color: row.status === 'approved' ? '#FFFFFF' : '#F8FAFC',
                                            minWidth: '70px', textAlign: 'center', display: 'inline-block'
                                        }}>
                                            {row.status.toUpperCase()}
                                        </span>
                                        <button
                                            onClick={() => handleStatusToggle(row.userId, row.status, row.userName)}
                                            style={{
                                                ...styles.actionBtn,
                                                backgroundColor: '#0D9488',
                                                color: row.status === 'approved' ? '#F59E0B' : '#FFFFFF',
                                                padding: '4px 8px', fontSize:'0.75rem', minWidth:'80px'
                                            }}
                                        >
                                            {row.status === 'approved' ? 'SUSPEND' : 'REINSTATE'}
                                        </button>
                                    </div>
                                </td>

                                <td style={{...styles.td, textAlign:'right'}}>
                                    <button
                                        onClick={() => handleRunReport(row.userId)}
                                        style={{
                                            ...styles.actionBtn,
                                            backgroundColor: '#0D9488',
                                            color: '#FFFFFF'
                                        }}
                                    >
                                        Report
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="9" style={{padding:'20px', textAlign:'center', color:'#475569', fontFamily: '"Inter", sans-serif'}}>No data found.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Inline Header & Search */}
            <div style={{marginBottom:'20px', display:'flex', justifyContent: 'space-between', alignItems:'center', flexWrap: 'wrap', gap: '15px'}}>
                <h2 style={{marginTop:0, marginBottom:0, color: '#163321', fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: '600'}}>User Management</h2>

                {/* Combined Badge & Input */}
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
                    <span style={{ backgroundColor: '#0D9488', color: '#FFFFFF', padding: '10px 15px', fontSize: '0.9rem', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}>Search for...</span>
                    <input
                        placeholder="Name or email..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        style={{ border: 'none', padding: '10px 15px', outline: 'none', fontSize: '0.9rem', fontFamily: '"Inter", sans-serif', width: '350px', color: '#0F172A' }}
                    />
                </div>
            </div>

            {renderGroup('Admin Users', adminRows)}
            {renderGroup('General Users', generalRows)}
        </div>
    );
};

const styles = {
    th: { padding: '10px 12px', cursor: 'pointer', userSelect: 'none', backgroundColor: '#163321', color: '#FFFFFF', verticalAlign: 'bottom', whiteSpace: 'pre-wrap', lineHeight: '1.2', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 10 },
    td: { padding: '10px 12px', verticalAlign: 'middle', color: '#0F172A', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' },
    input: { padding: '10px', width: '300px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem', color: '#0F172A' },
    actionBtn: { border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: '"Inter", sans-serif' }
};

export default UserManagementTable;